/**
 * md-tier-confirmation-dialog.tsx — Confirmation gate for MD-tier trigger assignment.
 *
 * AC 6: "MD tier is reserved for acute deterioration, repeated escalations, or scope-exceeded
 * cases — confirm this trigger requires Medical Director review before RD triage"
 *
 * Save is blocked until this dialog is confirmed. Dismissal (cancel) does NOT submit.
 */

import type { JSX } from 'react';

interface MdTierConfirmationDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function MdTierConfirmationDialog({
  open,
  onConfirm,
  onCancel,
}: MdTierConfirmationDialogProps): JSX.Element | null {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="md-tier-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h2 id="md-tier-dialog-title" className="text-lg font-semibold text-gray-900 mb-4">
          Confirm MD Tier Assignment
        </h2>
        <p className="text-sm text-gray-700 mb-6">
          MD tier is reserved for acute deterioration, repeated escalations, or scope-exceeded cases
          — confirm this trigger requires Medical Director review before RD triage
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid="md-tier-confirm-button"
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
          >
            Confirm MD Tier
          </button>
        </div>
      </div>
    </div>
  );
}
